import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const links = [
  { to: "/", label: "首页" },
  { to: "/adopt", label: "农场认种" },
  { to: "/smart-adopt", label: "智慧认种" },
  { to: "/farm", label: "种植管理" },
  { to: "/experience", label: "农场体验" },
  { to: "/feature", label: "农文旅专题" },
  { to: "/solution", label: "智慧农场" },
  { to: "/community", label: "圈层社交" },
  { to: "/circle", label: "社区交友" },
  { to: "/food-street", label: "美食街种植基地" },
  { to: "/portal", label: "用户端口" },
  { to: "/urban", label: "城市田园改造" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await signOut();
    setOpen(false);
    navigate("/");
  };

  const displayName = profile?.display_name || user?.email?.split("@")[0] || "会员";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        <NavLink to="/" className="flex items-center gap-2 cursor-pointer">
          <span
            className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center ${
              scrolled ? "bg-primary-500" : "bg-white/20 backdrop-blur"
            }`}
          >
            <i className="ri-leaf-fill text-lg md:text-xl text-white"></i>
          </span>
          <span
            className={`font-heading text-base md:text-lg font-semibold whitespace-nowrap ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            味聚华夏农耕酵素农场
          </span>
        </NavLink>

        <div className="hidden md:flex items-center gap-5">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? scrolled
                      ? "text-primary-600"
                      : "text-accent-400"
                    : scrolled
                    ? "text-foreground-700 hover:text-primary-600"
                    : "text-white/90 hover:text-white"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}

          {!loading && user ? (
            <div className="flex items-center gap-3">
              <span
                className={`flex items-center gap-2 cursor-pointer ${
                  scrolled ? "text-foreground-800" : "text-white"
                }`}
              >
                <span className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-semibold">
                  {displayName.slice(0, 1)}
                </span>
                <span className="text-sm font-medium whitespace-nowrap">{displayName}</span>
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="text-sm font-medium px-4 py-2 rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                退出
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <NavLink
                to="/login"
                className={`text-sm font-medium whitespace-nowrap cursor-pointer ${
                  scrolled ? "text-foreground-700 hover:text-primary-600" : "text-white/90 hover:text-white"
                }`}
              >
                登录
              </NavLink>
              <NavLink
                to="/adopt"
                className="text-sm font-medium px-5 py-2 rounded-md bg-accent-500 text-foreground-950 hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即认种
              </NavLink>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          aria-label="菜单"
        >
          <i
            className={`text-2xl ${scrolled ? "text-foreground-950" : "text-white"} ${
              open ? "ri-close-line" : "ri-menu-line"
            }`}
          ></i>
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-background-200 px-4 py-4 flex flex-col gap-3">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `text-sm font-medium py-1 cursor-pointer ${
                  isActive ? "text-primary-600" : "text-foreground-700"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}

          {!loading && user ? (
            <div className="flex items-center justify-between pt-2 border-t border-background-200">
              <span className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-semibold">
                  {displayName.slice(0, 1)}
                </span>
                <span className="text-sm font-medium text-foreground-800">{displayName}</span>
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="text-sm font-medium px-4 py-2 rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              >
                退出
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 pt-2 border-t border-background-200">
              <NavLink
                to="/login"
                onClick={() => setOpen(false)}
                className="flex-1 text-center text-sm font-medium px-5 py-2 rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              >
                登录
              </NavLink>
              <NavLink
                to="/adopt"
                onClick={() => setOpen(false)}
                className="flex-1 text-center text-sm font-medium px-5 py-2 rounded-md bg-accent-500 text-foreground-950 cursor-pointer"
              >
                立即认种
              </NavLink>
            </div>
          )}
        </div>
      )}
    </header>
  );
}